(() => {
  'use strict';
  if (window.MiravelGiftCards) return;
  const expected = ['mother-daughter','mother-son','grandma-granddaughter','husband-wife','big-sis-lil-sis','bestie-me'];
  const orderCardNames = Object.freeze({"none": "No message card", "mother-daughter": "Mother & Daughter", "mother-son": "Mother & Son", "grandma-granddaughter": "Grandma & Granddaughter", "husband-wife": "Husband & Wife", "big-sis-lil-sis": "Big Sis & Lil Sis", "bestie-me": "Bestie & Me"});
  const mounted = new WeakMap();
  const configurations = new WeakMap();
  const imageCache = new Map();
  const scheduledConfigurations = new WeakSet();
  function warmImages(cards, priority) {
    for (const card of cards) {
      const cached = imageCache.get(card.image);
      if (cached && cached.status !== 'error') {
        if (priority !== 'low' && cached.status === 'loading') cached.image.fetchPriority = priority;
        continue;
      }
      const img = new Image();
      const entry = {image:img, status:'loading', ready:null};
      entry.ready = new Promise(resolve => {
        img.onload = () => {
          const decoded = typeof img.decode === 'function' ? img.decode().catch(() => {}) : Promise.resolve();
          decoded.then(() => {entry.status = 'ready'; resolve(true);});
        };
        img.onerror = () => {entry.status = 'error'; resolve(false);};
      });
      imageCache.set(card.image, entry);
      img.decoding = 'async';
      img.fetchPriority = priority;
      img.src = card.image;
    }
  }
  function scheduleWarmCache(config) {
    if (scheduledConfigurations.has(config)) return;
    scheduledConfigurations.add(config);
    const preload = () => warmImages(config.cards, 'low');
    if ('requestIdleCallback' in window) requestIdleCallback(preload, {timeout:1600});
    else setTimeout(preload, 180);
  }
  let serial = 0;
  const element = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = 'mv-gift-card-select__' + className;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  function configFor(host) {
    const scope = host.closest('[data-veria-gift-duo]') || document;
    const source = scope.querySelector('[data-mv-gift-card-config]');
    if (!source) throw new Error('Gift message-card configuration is missing.');
    if (configurations.has(source)) return configurations.get(source);
    const config = JSON.parse(source.textContent);
    if (!config.productId || !config.version || !Array.isArray(config.cards) ||
        config.cards.length !== 6 || expected.some(id => config.cards.filter(c => c.id === id).length !== 1) ||
        config.cards.some(c => !c.name || !c.image) ||
        ['label','none','view','close','message'].some(k => !config.strings?.[k] || /translation missing/i.test(config.strings[k]))) {
      throw new Error('Gift message-card configuration is incomplete.');
    }
    configurations.set(source, config);
    return config;
  }
  function mount({host, slot, disabled = () => false, initialValue} = {}) {
    if (!(host instanceof HTMLElement) || !slot) throw new Error('Gift message-card host and slot are required.');
    if (mounted.has(host)) return mounted.get(host);
    const config = configFor(host), strings = config.strings;
    const cards = [{id:'none', name:strings.none, image:null}, ...config.cards];
    const storageKey = 'mv-gift-cards:v1:' + config.productId + ':' + slot;
    let saved = null;
    try { saved = sessionStorage.getItem(storageKey); } catch (_) {}
    let value = initialValue ?? saved ?? 'none';
    if (!cards.some(c => c.id === value)) throw new Error('Saved gift message-card selection is unavailable.');
    let forcedDisabled = false, destroyed = false, warm = false, dialog = null;
    const uid = 'mv-gift-card-' + (++serial);
    const wrap = element('div', 'control');
    const heading = element('div', 'heading');
    const label = element('label', 'label', strings.label); label.id = uid + '-label';
    const trigger = element('button', 'trigger'); trigger.type = 'button'; trigger.id = uid + '-trigger'; label.htmlFor = trigger.id;
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-labelledby', label.id + ' ' + uid + '-value');
    trigger.setAttribute('aria-controls', uid + '-dialog');
    heading.append(label); wrap.append(heading, trigger); host.append(wrap);
    host.classList.add('mv-gift-native-card-slot');
    const isDisabled = () => forcedDisabled || Boolean(disabled());
    function fillRow(row, card, isTrigger = false) {
      row.replaceChildren();
      if (card.image) {
        const img = element('img', 'thumb'); img.src = card.image; img.alt = ''; img.width = 27; img.height = 32;
        row.append(img);
      } else {
        const icon = element('span', 'empty', '—'); icon.setAttribute('aria-hidden','true'); row.append(icon);
      }
      const name = element('span', 'name', card.name); if (isTrigger) name.id = uid + '-value'; row.append(name);
      if (isTrigger) {const arrow = element('span', 'chevron'); arrow.setAttribute('aria-hidden','true'); row.append(arrow);}
    }
    function properties() {
      const card = cards.find(c => c.id === value);
      if (!card) throw new Error('Gift message-card selection is invalid.');
      return {'Free message card':orderCardNames[card.id], _mv_gift_card:card.id, _mv_gift_card_id:card.id, _mv_gift_card_version:config.version, _mv_gift_view:'gift'};
    }
    function update() {
      fillRow(trigger, cards.find(c => c.id === value), true);
      trigger.disabled = isDisabled();
      if (dialog) for (const button of dialog.querySelectorAll('[data-card-id]')) {
        button.setAttribute('aria-pressed', String(button.dataset.cardId === value));
        button.disabled = isDisabled();
      }
    }
    function warmCache() {
      if (warm || destroyed) return;
      warm = true;
      warmImages(config.cards, 'auto');
    }
    function select(id, emit = true) {
      if (!cards.some(c => c.id === id)) throw new Error('Unknown gift message-card selection.');
      value = id;
      try {sessionStorage.setItem(storageKey, value);} catch (_) {}
      update();
      if (emit) host.dispatchEvent(new CustomEvent('miravel:gift-card-change', {bubbles:true, detail:{slot,id:value,properties:properties()}}));
    }
    function openDialog() {
      if (isDisabled() || destroyed) return;
      warmCache();
      if (!dialog) {
        dialog = element('dialog','dialog'); dialog.id = uid + '-dialog'; dialog.setAttribute('aria-labelledby',uid+'-dialog-title');
        const top = element('div','dialog-heading');
        const title = element('h2','',strings.label); title.id = uid+'-dialog-title';
        const close = element('button','close','×'); close.type='button'; close.setAttribute('aria-label',strings.close);
        close.addEventListener('click',()=>dialog.close());
        top.append(title,close);
        const message = element('p','message',strings.message);
        const grid = element('div','grid');
        for (const card of config.cards) {
          const button = element('button','card'); button.type='button'; button.dataset.cardId=card.id;
          button.setAttribute('aria-pressed',String(card.id===value));
          const img=element('img'); img.src=card.image; img.alt=''; img.width=card.width; img.height=card.height; img.decoding='async';
          button.append(img,element('span','',card.name));
          button.addEventListener('click',()=>{if(isDisabled())return;select(card.id);dialog.close();});
          grid.append(button);
        }
        const none = element('button','none',strings.none);
        none.type = 'button'; none.dataset.cardId = 'none';
        none.addEventListener('click',()=>{if(isDisabled())return;select('none');dialog.close();});
        dialog.append(top,message,grid,none); document.body.append(dialog);
        dialog.addEventListener('close',()=>{trigger.setAttribute('aria-expanded','false');if(!destroyed&&trigger.isConnected)trigger.focus({preventScroll:true});});
        dialog.addEventListener('click',event=>{if(event.target===dialog){const r=dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)dialog.close();}});
      }
      update(); if (!dialog.open) dialog.showModal(); dialog.scrollTop = 0; trigger.setAttribute('aria-expanded','true'); dialog.querySelector('button').focus({preventScroll:true});
    }
    trigger.addEventListener('click',openDialog);
    trigger.addEventListener('keydown',event=>{if(event.key==='ArrowDown'||event.key==='ArrowUp'){event.preventDefault();openDialog();}});
    trigger.addEventListener('pointerenter',warmCache,{once:true});
    trigger.addEventListener('focus',warmCache,{once:true});
    const api = {
      getProperties:properties,
      getValue:()=>value,
      setValue:id=>select(id),
      setDisabled(value){forcedDisabled=Boolean(value);update();if(isDisabled()){if(dialog?.open)dialog.close();}},
      destroy(){destroyed=true;if(dialog?.open)dialog.close();dialog?.remove();wrap.remove();mounted.delete(host);}
    };
    mounted.set(host,api); update(); scheduleWarmCache(config); return api;
  }
  window.MiravelGiftCards = Object.freeze({mount});
  document.dispatchEvent(new CustomEvent('miravel:gift-cards-ready'));
})();
